import { Injectable, computed, signal, type Signal, type WritableSignal } from '@angular/core';

/**
 * Shared UI state for the drive-mode "CommentDrive" panel. The top-nav toggle
 * button writes the active state here; the drive project surface reads it to
 * react to the panel being opened/closed. Root-scoped so the top-nav and the
 * lazily-loaded drive surface observe the exact same signal.
 */
@Injectable({ providedIn: 'root' })
export class DriveCommentPanelService {
  private readonly _active: WritableSignal<boolean> = signal(false);
  private readonly _savedSource: WritableSignal<Signal<boolean | undefined> | null> = signal(null);

  /** Whether the CommentDrive panel is currently active/open. */
  readonly active: Signal<boolean> = this._active.asReadonly();

  /**
   * The panel's SAVED state, for the `cm` view-context flag: the drive surface's bound
   * source (the explorer's `savedShowSidebar`), falling back to {@link active} while it
   * has nothing to say. It differs from `active` only while the feed is in drive's
   * unsaved narrow-load close, which no shared link should carry.
   */
  readonly savedActive: Signal<boolean> = computed(() => this._savedSource()?.() ?? this._active());

  /** Flips the active state — used by the top-nav CommentDrive button. */
  public toggle(): void {
    this._active.update((active: boolean) => !active);
  }

  public setActive(active: boolean): void {
    this._active.set(active);
  }

  public bindSavedSource(source: Signal<boolean | undefined>): void {
    this._savedSource.set(source);
  }

  /** Identity-checked so a late destroy never clobbers a newer surface's binding. */
  public unbindSavedSource(source: Signal<boolean | undefined>): void {
    if (this._savedSource() === source) {
      this._savedSource.set(null);
    }
  }
}
